import React from 'react'
import { Image, StyleSheet, Text, View } from '@react-pdf/renderer'

/**
 * Default text style configuration
 */
export const DEFAULT_TEXT_STYLE = {
  fontFamily: 'Helvetica',
  fontSize: 10,
  color: '#2d3748',
} as const

/**
 * PDF element styles
 */
export const elementStyles = StyleSheet.create({
  // Image styles
  image: {
    maxWidth: 100,
    objectFit: 'contain',
    height: 'auto',
    marginVertical: 4,
  },
})

/**
 * Creates a text component with standard styling
 * @param content - Text content
 * @param key - React key
 * @param marginBottom - Bottom margin (optional)
 * @returns React Text element
 */
export function createTextComponent(content: string, key: string, marginBottom: number = 4): React.ReactElement {
  return React.createElement(
    Text,
    {
      key,
      style: {
        ...DEFAULT_TEXT_STYLE,
        marginBottom,
      },
    },
    content
  )
}

/**
 * Creates an image component
 * @param src - Image URL
 * @param key - React key
 * @returns React Image element
 */
export function createImageComponent(src: string, key: string): React.ReactElement {
  return React.createElement(Image, {
    key,
    src,
    style: elementStyles.image,
  })
}

/**
 * Creates an empty spacing component
 * @param key - React key
 * @param height - Height in pixels (default: 12)
 * @returns React View element for spacing
 */
export function createSpacingComponent(key: string, height: number = 12): React.ReactElement {
  return React.createElement(View, {
    key,
    style: { height },
  })
}

/**
 * Regular expression to match image URLs with common extensions
 */
export const IMAGE_URL_REGEX = /https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|bmp|webp|svg)(?:\?[^\s]*)?/gi

/**
 * Checks if a string contains a valid image URL
 * @param text - Text to check for image URLs
 * @returns Array of image URLs found in the text
 */
export function extractImageUrls(text: string): string[] {
  const matches = text.match(IMAGE_URL_REGEX)
  return matches || []
}
